import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HeroDither from '../components/shared/HeroDither';
import Reveal, { REVEAL_CSS } from '../components/shared/Reveal';
import useSeoMeta from '../components/shared/useSeoMeta';
import RelatedPagesLinks from '../components/shared/RelatedPagesLinks';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconLock = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

const IconKey = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.778-7.778zm0 0L15.5 7.5m0 0L19 4m-3.5 3.5L19 11"/>
  </svg>
);

const IconUsers = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const IconCloud = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
  </svg>
);

const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const SECURITY_CARDS = [
  { icon: <IconLock/>, title: 'Cifrado en tránsito', desc: 'Toda la plataforma funciona sobre HTTPS. Tus datos viajan cifrados en cada solicitud, siempre.' },
  { icon: <IconKey/>, title: 'Autenticación por token', desc: 'Cada sesión se valida con un token firmado (JWT), no con una cookie plana que cualquiera pueda reutilizar.' },
  { icon: <IconUsers/>, title: 'Acceso por roles', desc: 'Un administrador ve lo que necesita administrar. Un miembro de equipo ve solo su perfil y sus leads, nada más.' },
  { icon: <IconCloud/>, title: 'Copias de seguridad automáticas', desc: 'Tu información se respalda de forma continua, para que nunca dependa de un solo servidor.' },
  { icon: <IconShield/>, title: 'Límite de frecuencia', desc: 'Los formularios públicos y el chat tienen límites de uso para frenar abuso automatizado antes de que ocurra.' },
];

const FAQS = [
  { q: '¿Mis datos se comparten con terceros?', a: 'No, tu información no se vende ni se comparte con fines publicitarios.' },
  { q: '¿Qué pasa si alguien de mi equipo se va?', a: 'Como administrador puedes desactivar su acceso de inmediato desde el panel.' },
  { q: '¿Dónde veo las políticas completas?', a: 'En Términos, Privacidad y Tratamiento de Datos, disponibles en español, inglés y portugués.' },
];

const PAGE_TITLE = 'Seguridad y Privacidad de tus Datos de Contacto | Contact';
const PAGE_DESCRIPTION = 'Cifrado en tránsito, autenticación por token, control de acceso por roles y copias de seguridad automáticas. Así protegemos tu información en Contact.';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const SeguridadPage = () => {
  const navigate = useNavigate();
  useSeoMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/seguridad' });

  return (
    <div className="sg-root">
      <HeroDither/>
      <header className="sg-nav">
        <div className="sg-nav-inner">
          <ContactLogo clickable/>
          <div className="sg-nav-actions">
            <button onClick={() => navigate('/')} className="sg-btn-back">
              <IconArrowLeft/> Volver
            </button>
            <button onClick={() => navigate('/comprar')} className="sg-btn-comprar-nav">Comprar</button>
            <button onClick={() => navigate('/login')} className="sg-btn-acceder">Acceder</button>
          </div>
        </div>
      </header>

      <section className="sg-hero">
        <div className="sg-container">
          <p className="sg-eyebrow">Seguridad</p>
          <h1 className="sg-h1">Pensado para equipos que gestionan contactos todos los días.</h1>
          <p className="sg-lead">
            Cada perfil que compartes incluye información real sobre ti y tu empresa. La tratamos con
            el mismo cuidado con el que esperarías que alguien tratara la tuya.
          </p>
        </div>
      </section>

      <section className="sg-section">
        <div className="sg-container">
          <Reveal>
          <h2 className="sg-h2">Medidas reales, no solo una promesa en la letra chica.</h2>
          <div className="sg-grid">
            {SECURITY_CARDS.map((c) => (
              <div key={c.title} className="sg-card">
                <span className="sg-card-icon">{c.icon}</span>
                <h3 className="sg-card-title">{c.title}</h3>
                <p className="sg-card-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="sg-section">
        <div className="sg-container">
          <Reveal>
          <h2 className="sg-h2 sg-h2--tight">Tus datos son tuyos, en todo momento.</h2>
          <div className="sg-privacy-grid">
            <div className="sg-privacy-item">
              <h4 className="sg-privacy-title">No vendemos tus datos</h4>
              <p className="sg-privacy-desc">Tu información no se comparte ni se vende a terceros con fines publicitarios.</p>
            </div>
            <div className="sg-privacy-item">
              <h4 className="sg-privacy-title">Tú decides qué se muestra</h4>
              <p className="sg-privacy-desc">Eliges exactamente qué campos aparecen en tu perfil público y cuáles no.</p>
            </div>
            <div className="sg-privacy-item">
              <h4 className="sg-privacy-title">Políticas propias y claras</h4>
              <p className="sg-privacy-desc">Nuestros Términos, Política de Privacidad y Tratamiento de Datos están disponibles en español, inglés y portugués.</p>
            </div>
          </div>
          <div className="sg-legal-links">
            <button onClick={() => navigate('/terminos')} className="sg-legal-link">Términos de uso</button>
            <button onClick={() => navigate('/privacidad')} className="sg-legal-link">Política de privacidad</button>
            <button onClick={() => navigate('/tratamiento-de-datos')} className="sg-legal-link">Tratamiento de datos</button>
          </div>
          </Reveal>
        </div>
      </section>

      <section className="sg-section">
        <div className="sg-container">
          <Reveal>
          <h2 className="sg-h2 sg-h2--tight">Preguntas frecuentes.</h2>
          <div className="sg-faq-list">
            {FAQS.map((f) => (
              <div key={f.q} className="sg-faq-item">
                <h4 className="sg-faq-q">{f.q}</h4>
                <p className="sg-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="sg-cta">
        <div className="sg-container sg-cta-inner">
          <h2 className="sg-h2">Comparte tu perfil con tranquilidad.</h2>
          <p className="sg-p sg-cta-sub">Crea tu perfil gratis, con la misma seguridad para todos los planes.</p>
          <div className="sg-cta-actions">
            <button onClick={() => navigate('/register')} className="sg-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="sg-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      <footer className="sg-footer">
        <div className="sg-container">
          <div className="sg-related-row">
            <RelatedPagesLinks currentPath="/seguridad" linkClassName="sg-related-link" />
          </div>
          <div className="sg-footer-inner">
            <ContactLogo/>
            <span className="sg-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <style>{CSS}</style>
      <style>{REVEAL_CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --sg-bg:#F9F9F8;
  --sg-bg-w:#FFFFFF;
  --sg-text:#000000;
  --sg-muted:#4a4a4a;
  --sg-faint:#C0C0C0;
  --sg-border:#EEEEEC;
  --sg-border-dk:#CCCCCA;
}

.sg-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--sg-bg);color:var(--sg-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}

.sg-nav{position:sticky;top:0;z-index:100;background:transparent}
.sg-nav-inner{max-width:900px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between}
.sg-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--sg-muted);font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.sg-btn-back:hover{color:var(--sg-text)}
.sg-nav-actions{display:flex;align-items:center;gap:10px}
.sg-btn-acceder{display:inline-flex;align-items:center;background:var(--sg-text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
.sg-btn-acceder:hover{opacity:.82}
.sg-btn-comprar-nav{display:inline-flex;align-items:center;background:transparent;color:var(--sg-text);border:1.5px solid var(--sg-border-dk);border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s}
.sg-btn-comprar-nav:hover{border-color:var(--sg-text)}

.sg-container{max-width:900px;margin:0 auto;padding:0 40px}

.sg-hero{padding:72px 0 56px}
.sg-eyebrow{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;color:var(--sg-muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.sg-h1{font-weight:300;font-size:clamp(32px,4.6vw,52px);line-height:1.15;letter-spacing:-.025em;color:var(--sg-text);margin-bottom:26px;max-width:720px}
.sg-lead{font-family:'Inter',system-ui,sans-serif;font-size:clamp(15px,1.6vw,17px);color:var(--sg-muted);line-height:1.75;max-width:640px}

.sg-section{padding:0 0 80px}
.sg-h2{font-weight:300;font-size:clamp(22px,2.6vw,30px);line-height:1.3;letter-spacing:-.02em;color:var(--sg-text);margin-bottom:32px;max-width:620px}
.sg-h2--tight{margin-bottom:28px}
.sg-p{font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--sg-muted);line-height:1.75;max-width:560px}

.sg-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--sg-border);border:1px solid var(--sg-border)}
.sg-card{background:var(--sg-bg-w);padding:26px}
.sg-card-icon{display:inline-flex;color:var(--sg-text);margin-bottom:14px}
.sg-card-title{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--sg-text);margin-bottom:8px;letter-spacing:-.01em}
.sg-card-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--sg-muted);line-height:1.65}

.sg-privacy-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-bottom:32px}
.sg-privacy-item{border-top:2px solid var(--sg-text);padding-top:16px}
.sg-privacy-title{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--sg-text);margin-bottom:8px}
.sg-privacy-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--sg-muted);line-height:1.65}

.sg-legal-links{display:flex;flex-wrap:wrap;gap:12px}
.sg-legal-link{background:none;border:1px solid var(--sg-border-dk);border-radius:100px;padding:8px 16px;color:var(--sg-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.sg-legal-link:hover{color:var(--sg-text);border-color:var(--sg-text)}

.sg-faq-list{display:flex;flex-direction:column;gap:22px;max-width:640px}
.sg-faq-item{border-top:1px solid var(--sg-border);padding-top:16px}
.sg-faq-q{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--sg-text);margin-bottom:8px}
.sg-faq-a{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--sg-muted);line-height:1.65}

.sg-cta{padding:0 0 80px}
.sg-cta-inner{background:var(--sg-bg-w);border:1px solid var(--sg-border);border-radius:24px;padding:60px 48px;text-align:center;display:flex;flex-direction:column;align-items:center}
.sg-cta-sub{text-align:center;margin:0 auto 32px}
.sg-cta-actions{display:flex;align-items:center;gap:14px}
.sg-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--sg-text);color:#fff;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.sg-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.sg-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--sg-text);border:1.5px solid var(--sg-border-dk);border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.sg-btn-outline:hover{border-color:var(--sg-text);background:var(--sg-bg)}

.sg-related-row{display:flex;flex-wrap:wrap;gap:10px;padding:28px 0;border-top:1px solid var(--sg-border)}
.sg-related-link{background:none;border:1px solid var(--sg-border-dk);border-radius:100px;padding:7px 15px;color:var(--sg-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.sg-related-link:hover{color:var(--sg-text);border-color:var(--sg-text)}

.sg-footer-inner{display:flex;align-items:center;justify-content:space-between;padding:24px 0}
.sg-footer-copy{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:var(--sg-faint)}

@media(max-width:640px){
  .sg-nav-inner,.sg-container{padding:0 20px}
  .sg-nav-actions{gap:6px}
  .sg-btn-acceder,.sg-btn-comprar-nav{height:32px;padding:0 12px;font-size:12px}
  .sg-btn-back{font-size:0}
  .sg-hero{padding:48px 0 40px}
  .sg-section,.sg-cta{padding-bottom:56px}
  .sg-grid,.sg-privacy-grid{grid-template-columns:1fr}
  .sg-cta-inner{padding:44px 22px}
  .sg-cta-actions{flex-direction:column;width:100%}
  .sg-cta-actions .sg-btn-pill,.sg-cta-actions .sg-btn-outline{width:100%;justify-content:center}
  .sg-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default SeguridadPage;
